import { OrderV2 } from '@kotipizzagroup/kotipizza-orders-api-client';
import { alpha, Theme } from '@mui/material';
import { getOrderPaymentState, OrderPaymentState } from '../../utils/orderUtils';

// A plain enum rather than `const enum`: tsconfig has `isolatedModules`, so esbuild cannot inline
// const enum members across module boundaries.
export enum OrderStatus {
  Cancelled = 'Cancelled',
  Done = 'Done',
  Delivered = 'Delivered',
  InDelivery = 'In delivery',
  ReadyForDelivery = 'Ready for delivery',
  InTheOven = 'In the oven',
  Accepted = 'Accepted',
  WaitingForConfirmation = 'Waiting for confirmation',
  WaitingForPayment = 'Waiting for payment',
  PaymentFailed = 'Payment failed',
  PaymentReceivedTooLate = 'Payment received too late',
  Unknown = 'Unknown state',
}

type Colors = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export const OrderStatusColors: Record<OrderStatus, Colors> = {
  [OrderStatus.Cancelled]: 'error',
  [OrderStatus.Done]: 'success',
  [OrderStatus.Delivered]: 'success',
  [OrderStatus.InDelivery]: 'secondary',
  [OrderStatus.ReadyForDelivery]: 'secondary',
  [OrderStatus.InTheOven]: 'secondary',
  [OrderStatus.Accepted]: 'secondary',
  [OrderStatus.WaitingForConfirmation]: 'warning',
  [OrderStatus.WaitingForPayment]: 'warning',
  [OrderStatus.PaymentFailed]: 'error',
  [OrderStatus.PaymentReceivedTooLate]: 'error',
  [OrderStatus.Unknown]: 'info',
};

// Shared by the desktop chip and the phone pill so both render a status in the same tint.
export const orderStatusBackground = (theme: Theme, status: OrderStatus): string => {
  const tone = OrderStatusColors[status];
  return alpha(tone === 'default' ? theme.palette.neutral.main : theme.palette[tone].main, 0.4);
};

/**
 * The colour a status reads as when it is set as plain text rather than filled behind a chip — which
 * is how the phone rows show it.
 *
 * Driven by `OrderStatusColors`, so a row, the grid's chip and the details sheet can never disagree
 * about which statuses are the urgent ones. Two of its tones have no text colour behind them, though:
 * the theme palette defines no `secondary`, so the in-progress statuses would resolve to MUI's default
 * purple, and `default` is not a palette key at all. Both fall back to the body colour — a chip could
 * carry them as a faint fill, but as text they would be a hue with no meaning attached. That suits
 * what they mean: "under way" is the unremarkable case, and the eye should be caught by the statuses
 * that are not.
 */
export const orderStatusTextColor = (theme: Theme, status: OrderStatus): string => {
  const tone = OrderStatusColors[status];

  return tone === 'default' || tone === 'secondary' ? theme.palette.text.primary : theme.palette[tone].main;
};

/**
 * The statuses ranked in the order a shift lives through them, so sorting by status groups the list
 * the way the kitchen reads it: what still needs money or a decision first, finished and cancelled
 * orders last.
 */
export const ORDER_STATUS_RANK: Record<OrderStatus, number> = {
  [OrderStatus.WaitingForPayment]: 0,
  [OrderStatus.PaymentFailed]: 1,
  [OrderStatus.PaymentReceivedTooLate]: 2,
  [OrderStatus.WaitingForConfirmation]: 3,
  [OrderStatus.Accepted]: 4,
  [OrderStatus.InTheOven]: 5,
  [OrderStatus.ReadyForDelivery]: 6,
  [OrderStatus.InDelivery]: 7,
  [OrderStatus.Delivered]: 8,
  [OrderStatus.Done]: 9,
  [OrderStatus.Cancelled]: 10,
  [OrderStatus.Unknown]: 11,
};

export const orderStatusFromTimeStamps = (order: OrderV2): OrderStatus => {
  if (order.OrderCancelledTime) return OrderStatus.Cancelled;
  if (order.ProcessFinished) return OrderStatus.Done;
  if (order.s4dDeliveryDeliveryComplete) return OrderStatus.Delivered;
  if (order.s4dDeliveryInDelivery) return OrderStatus.InDelivery;
  if (order.ReadyForDeliveryTime) return OrderStatus.ReadyForDelivery;
  if (order.RegisterManufacturingTime) return OrderStatus.InTheOven;
  if (order.RegisterAcceptedTime) return OrderStatus.Accepted;

  const orderPaymentState = getOrderPaymentState(order);

  if (orderPaymentState === OrderPaymentState.Paid || orderPaymentState === OrderPaymentState.PaidInRestaurant) {
    return OrderStatus.WaitingForConfirmation;
  }

  if (orderPaymentState === OrderPaymentState.Unpaid) {
    return OrderStatus.WaitingForPayment;
  }

  if (orderPaymentState === OrderPaymentState.PaymentNotCompleted) {
    return OrderStatus.PaymentFailed;
  }

  if (orderPaymentState === OrderPaymentState.PaymentReceivedTooLate) {
    return OrderStatus.PaymentReceivedTooLate;
  }

  return OrderStatus.Unknown;
};
